import React, { useEffect, useState, Dispatch } from 'react';
import { Text, InfoOutlined, Tooltip, Box } from '@landr/maestro';
import { PlanFrequencyEnum, PrismicPrice } from '@landr/core.models';
import { Currency, isCommission, Price } from '@landr/pricing';
import { CountryCurrency } from 'helpers';
import {
    StyledPlanDetailsList,
    StyledPlanDetailsListItem,
    StyledProductPrice,
    StyledPlanDetails,
    StyledPlanFeatures,
} from '../styled';
import { PricingPlanDocument, PricingPlanProductDocument, PricingPlanFeatureDocument } from '../types';
import { getPlanFrequency, getProductPrice } from '../common/helpers';
import { usePricingContext } from '../common/PricingContext';

export type ProductPriceProps = { prismicPrice: PrismicPrice; unlimitedText: string };

const ProductPrice: React.FC<ProductPriceProps> = ({ prismicPrice, unlimitedText }) => {
    const price = new Price(prismicPrice);

    return (
        <>
            {prismicPrice.amount || isCommission(prismicPrice.currencySymbol) ? (
                <StyledProductPrice>
                    <Currency amount={prismicPrice.amount} price={price} />
                </StyledProductPrice>
            ) : (
                <StyledProductPrice>{unlimitedText}</StyledProductPrice>
            )}
        </>
    );
};

export type PricingPlanFeature = {
    sub_features: [{ label: string; tooltip: string }];
    title: string;
};

export type PricingPlanDetailsProps = {
    plan: PricingPlanDocument;
    frequency: PlanFrequencyEnum;
    countryCurrency: CountryCurrency;
};

export const PricingPlanDetails: React.FC<PricingPlanDetailsProps> = ({ plan, frequency, countryCurrency }) => {
    const { translations } = usePricingContext();

    const [planProducts, setPlanProducts]: [
        PricingPlanProductDocument[],
        Dispatch<PricingPlanProductDocument[]>,
    ] = useState<PricingPlanProductDocument[]>([]);

    const [planFeatures, setPlanFeatures]: [
        PricingPlanFeatureDocument[],
        Dispatch<PricingPlanFeatureDocument[]>,
    ] = useState<PricingPlanFeatureDocument[]>([]);

    const pricingPlanProductsWithPrices = plan.products
        .filter(({ product }) => product && (product as PricingPlanProductDocument).name)
        .map(({ product }: { product: PricingPlanProductDocument }) => {
            const { code } = product.api_product;
            const apiPlanFrequency = getPlanFrequency(plan.api_plan, frequency);
            const price = getProductPrice(apiPlanFrequency, code, countryCurrency);
            return {
                ...product,
                price,
            };
        });

    useEffect(() => {
        pricingPlanProductsWithPrices && setPlanProducts(pricingPlanProductsWithPrices);
    }, [plan, frequency, countryCurrency]);

    const pricingPlanFeatures = plan.products
        .filter(({ product }) => product && (product as PricingPlanFeatureDocument).title)
        .map(({ product }: { product: PricingPlanFeatureDocument }) => product);

    useEffect(() => {
        pricingPlanFeatures && setPlanFeatures(pricingPlanFeatures);
    }, [plan]);

    return (
        <StyledPlanDetails>
            <StyledPlanDetailsList>
                {planProducts &&
                    planProducts.map(({ name, unlimited_text, tooltip, price }, index) => {
                        return (
                            <StyledPlanDetailsListItem key={`pricing-plan-product-${index}`}>
                                <Text size="md" marginRight="xs" contrast="low">
                                    {name}
                                </Text>
                                {tooltip && tooltip[0] && (
                                    <Tooltip position="top" content={tooltip[0].text}>
                                        <InfoOutlined />
                                        <Box as="span" aria-label={`product-tooltip-${name}`} />
                                    </Tooltip>
                                )}
                                <ProductPrice prismicPrice={price} unlimitedText={unlimited_text}></ProductPrice>
                            </StyledPlanDetailsListItem>
                        );
                    })}
            </StyledPlanDetailsList>
            {planFeatures &&
                planFeatures.map(({ sub_features }, index) => {
                    return (
                        <StyledPlanFeatures key={`plan-feature-${index}`}>
                            <Text size="sm" mt={24} mb={10} contrast="low">
                                {translations.mastering_features_name}
                            </Text>
                            <StyledPlanDetailsList>
                                {sub_features.map(({ label, tooltip }, index) => {
                                    return (
                                        <StyledPlanDetailsListItem key={`sub-feature-${index}`}>
                                            <Text size="md" marginRight="xs">
                                                {label}
                                            </Text>
                                            <Tooltip position="top" content={tooltip}>
                                                <InfoOutlined />
                                                <Box as="span" aria-label={`product-feature-tooltip-${index}`} />
                                            </Tooltip>
                                        </StyledPlanDetailsListItem>
                                    );
                                })}
                            </StyledPlanDetailsList>
                        </StyledPlanFeatures>
                    );
                })}
        </StyledPlanDetails>
    );
};
